import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, Heart, Calendar, Users, Clock, 
    Dumbbell, Sparkles, Leaf, ChevronRight, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WellnessPage() {
    const navigate = useNavigate();
    const { organization, getTerm } = useOrganization();
    const { token, profile } = useAuth();
    const [activities, setActivities] = useState([]);
    const [wellnessServices, setWellnessServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');

    // Wellness-specific categories
    const wellnessCategories = [
        { id: 'all', label: 'All', icon: Heart },
        { id: 'move_well', label: 'Fitness', icon: Dumbbell },
        { id: 'feel_well', label: 'Mind & Body', icon: Sparkles },
    ];

    useEffect(() => {
        fetchWellnessData();
    }, [token]);

    const fetchWellnessData = async () => {
        try {
            // Fetch wellness activities
            const activitiesRes = await axios.get(`${API}/activities`);
            const wellnessActivities = activitiesRes.data.filter(a => 
                ['move_well', 'feel_well'].includes(a.category)
            );
            setActivities(wellnessActivities);

            // Fetch wellness services
            if (token) {
                const servicesRes = await axios.get(`${API}/welcome/wellness-services`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setWellnessServices(servicesRes.data);
            }
        } catch (error) {
            console.error('Error fetching wellness data:', error);
            // Set default services on error
            setWellnessServices([
                { id: '1', title: 'Physiotherapy', description: 'One-to-one sessions with qualified physiotherapist', bookable: true },
                { id: '2', title: 'Massage Therapy', description: 'Swedish, deep tissue, and relaxation massage', bookable: true },
                { id: '3', title: 'Beauty Treatments', description: 'Facials, manicures, pedicures, and more', bookable: true },
                { id: '4', title: 'Personal Training', description: 'Tailored fitness programmes with certified trainers', bookable: true },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const filteredActivities = activeCategory === 'all' 
        ? activities 
        : activities.filter(a => a.category === activeCategory);

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getCategoryColor = (category) => {
        return category === 'move_well' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700';
    };

    const getCategoryLabel = (category) => {
        return category === 'move_well' ? 'Fitness' : 'Mind & Body';
    };

    return (
        <div className="min-h-screen bg-stone-50 pb-24" data-testid="wellness-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-emerald-700 to-emerald-800 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-emerald-200 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Home
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Heart className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">Wellness & Care</h1>
                        <p className="text-white/80 text-sm">Health, fitness & wellbeing</p>
                    </div>
                </div>

                {/* Credits Display */}
                {profile && (
                    <div className="mt-4 bg-white/10 rounded-xl px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CreditCard className="w-5 h-5" />
                            <span className="text-sm">Your {getTerm('credits')}</span>
                        </div>
                        <span className="font-bold text-lg">{profile.credits_balance}</span>
                    </div>
                )}
            </div>

            {/* Category Filter */}
            <div className="px-4 -mt-4 relative z-10">
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-1 flex">
                    {wellnessCategories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                                activeCategory === cat.id
                                    ? 'bg-emerald-600 text-white'
                                    : 'text-stone-600 hover:bg-stone-100'
                            }`}
                        >
                            <cat.icon className="w-4 h-4" />
                            {cat.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Activities List */}
            <div className="px-4 mt-6">
                <h2 className="text-lg font-semibold text-stone-800 mb-4">Upcoming Classes</h2>
                
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : filteredActivities.length === 0 ? (
                    <div className="bg-white rounded-2xl p-8 text-center border border-stone-200">
                        <Heart className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                        <h3 className="font-semibold text-stone-700 mb-2">No Classes Available</h3>
                        <p className="text-stone-500 text-sm">Check back soon for new wellness activities.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredActivities.slice(0, 6).map((activity) => (
                            <div 
                                key={activity.id}
                                className="bg-white rounded-2xl p-4 border border-stone-200 cursor-pointer hover:shadow-md transition-shadow"
                                onClick={() => navigate(`/activities/${activity.id}`)}
                            >
                                <div className="flex items-start gap-4">
                                    {activity.image_url ? (
                                        <img 
                                            src={activity.image_url}
                                            alt={activity.title}
                                            className="w-16 h-16 rounded-xl object-cover"
                                        />
                                    ) : (
                                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${getCategoryColor(activity.category)}`}>
                                            {activity.category === 'move_well' ? (
                                                <Dumbbell className="w-6 h-6" />
                                            ) : (
                                                <Sparkles className="w-6 h-6" />
                                            )}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(activity.category)}`}>
                                                    {getCategoryLabel(activity.category)}
                                                </span>
                                                <h3 className="font-semibold text-stone-800 mt-1">{activity.title}</h3>
                                            </div>
                                            <span className="text-emerald-600 font-bold text-sm">
                                                {activity.credit_cost} {activity.credit_cost === 1 ? 'credit' : 'credits'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-4 mt-2 text-sm text-stone-500">
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-4 h-4" />
                                                {formatDate(activity.date_time)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Users className="w-4 h-4" />
                                                {activity.capacity - activity.current_bookings} spots
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {filteredActivities.length > 0 && (
                    <Button
                        variant="outline"
                        className="w-full mt-4"
                        onClick={() => navigate('/activities?category=wellness')}
                    >
                        View All Classes
                        <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                )}
            </div>

            {/* Bookable Services */}
            <div className="px-4 mt-8">
                <h2 className="text-lg font-semibold text-stone-800 mb-4">Bookable Services</h2>
                <div className="space-y-3">
                    {wellnessServices.map((service) => (
                        <div key={service.id} className="bg-white rounded-2xl p-4 border border-stone-200 flex items-center justify-between">
                            <div>
                                <h3 className="font-semibold text-stone-800">{service.title}</h3>
                                <p className="text-sm text-stone-500">{service.description}</p>
                                {service.price && (
                                    <p className="text-sm text-emerald-600 mt-1">{service.price}</p>
                                )}
                            </div>
                            {service.bookable && (
                                <Button variant="outline" size="sm" className="flex-shrink-0">
                                    Book
                                </Button>
                            )}
                        </div>
                    ))}
                </div>

                {/* AI Assistant Help */}
                <div 
                    onClick={() => navigate('/assistant')}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-5 mt-6 cursor-pointer hover:from-emerald-700 hover:to-emerald-800 transition-all"
                    data-testid="ai-assistant-help"
                >
                    <h3 className="font-semibold text-white mb-2">Need more assistance?</h3>
                    <p className="text-emerald-100 text-sm mb-3">
                        Our AI assistant can help with wellness queries and bookings.
                    </p>
                    <span className="text-white font-bold flex items-center gap-2">
                        Chat with Assistant
                        <ChevronRight className="w-4 h-4" />
                    </span>
                </div>
            </div>
        </div>
    );
}

export default WellnessPage;
